import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PropsWithChildren,
  type RefObject,
} from "react";

import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  ArrowDropDown,
  CheckCircle,
  ContentPaste,
  Lock,
  MonitorWeight,
  Receipt,
} from "@mui/icons-material";
import { Box, IconButton, Typography, alpha, useTheme } from "@mui/material";
import invariant from "tiny-invariant";
import type { ContextMenuRef } from "../../../../components/shared/ContextMenuDropdown";
import PlannedStatusTag from "../../../../components/shared/StatusTag/PlannedStatusTag";
import { TagsGrid } from "../../../../components/shared/TagsGrid";
import type { DraggedItem } from "../../../../components/shared/dragndrop/types";
import { iconFillColors } from "../../../../components/shared/icons/Icon.types";
import PalletsIcon from "../../../../components/shared/icons/PalletsIcon";
import PiecesIcon from "../../../../components/shared/icons/PiecesIcon";
import {
  Accordion,
  AccordionDetails,
} from "../../../../components/shared/layout/Accordion";
import Loading from "../../../../components/shared/layout/Loading";
import { ShipmentItem } from "../../../../components/shared/sidebar/ShipmentItem";
import TableLink from "../../../../components/shared/table/TableLink";
import { toastMessage } from "../../../../constants/strings";
import { formatNumber } from "../../../../constants/utils";
import { useSelectionContext } from "../../../../context/SelectionContext";
import {
  useAssignShipments,
  useUnassignShipments,
} from "../../../../hooks/react-query/useAssignShipments";
import { useIsRouteImmutable } from "../../../../hooks/useIsRouteImmutable";
import { useIsTrapCancelled } from "../../../../hooks/useIsTrapCancelled";
import { useLinkedFilters } from "../../../../hooks/useLinkedFilters";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import type {
  AssignShipmentFn,
  UnassignShipmentFn,
} from "../../../../types/assignShipment.type";
import type { Tag } from "../../../../types/freight/tag.type";
import type {
  InboundPages,
  InboundTabs,
} from "../../../../types/inbound/inboundView.type";
import type { PlanType } from "../../../../types/planning/plan.type";
import { dateToInt } from "../../../../utils/dateTimeHelper";
import { getDropMetrics } from "../../../../utils/dropHelpers";
import { toast } from "../../../../utils/snackbarHelper";
import { sortShipmentsByStopSequence } from "../../../../utils/sortComparator";
import { type ConfirmShipmentMoveDialogRef } from "../../inboundPlanning/ConfirmShipmentMoveDialog";
import { useInboundPlanningContext } from "../../inboundPlanning/InboundPlanningContext";
import {
  actionsMap,
  type ShipmentActionDialogRef,
  type ShipmentActionTypeId,
} from "../../inboundPlanning/ShipmentActionDialog";
import { useInboundDragAndDrop } from "../../inboundPlanning/useInboundDragAndDrop";
import {
  getAfterRouteStopParam,
  getFormattedPlannedRoutes,
  inboundFilterParamsToKeep,
} from "../../inboundPlanning/utils";
import type { SummaryContext, SummaryContextType } from "../ActionsMenuContext";
import { InboundTrailerIcon } from "../InboundTrailerIcon";
import type { tableSectionToIconMap } from "../TableActionBar";
import { formatTrailerTitle } from "../utils";
import {
  Item,
  ItemSummary,
  NoResultFound,
  Summary,
  Totals,
} from "./SectionItem";

type PlanningDroppableSectionItemData = {
  id: number;
  title?: string;
  name: string | null;
  bills: number;
  pallets: number;
  pieces: number;
  weight: number;
  weightUnit?: string;
  shipmentIds?: number[];
  tags?: Tag[];
  status?: string;
  allFuturePlanDates?: number[];
  allShipmentsPlannedForFuture?: boolean;
  allShipmentsPlanned?: boolean;
  isCommitted?: boolean | null;
  isPlanCompleted?: boolean | null;
};

interface PlanningDroppableSectionItemProps<T> {
  isEditingAllowed: boolean;
  selectedDate: string;
  data: T;
  contextRef?: RefObject<ContextMenuRef<SummaryContext>>;
  tab?: InboundTabs;
  isSelected?: boolean;
  canReorder?: boolean;
  icon?: keyof typeof tableSectionToIconMap;
  planType?: PlanType;
  inboundPage?: InboundPages;
  shipmentActionDialogRef?: RefObject<ShipmentActionDialogRef>;
  confirmDialogRef?: RefObject<ConfirmShipmentMoveDialogRef>;
  onSidebarRefresh?: () => Promise<void>;
  onClearSidebarSelection?: () => void;
}

const PlanningDroppableSectionItem = <
  T extends PlanningDroppableSectionItemData,
>({
  isEditingAllowed,
  canReorder = false,
  data,
  selectedDate,
  icon = "list",
  contextRef,
  tab,
  isSelected,
  planType,
  inboundPage,
  shipmentActionDialogRef,
  confirmDialogRef,
  onSidebarRefresh,
  onClearSidebarSelection,
}: PropsWithChildren<PlanningDroppableSectionItemProps<T>>) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const emptyContentRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();

  const {
    id,
    title,
    name,
    bills,
    pallets,
    pieces,
    weight,
    weightUnit,
    allFuturePlanDates,
    allShipmentsPlanned,
    allShipmentsPlannedForFuture,
    isCommitted,
    shipmentIds = [],
    status,
    tags,
    isPlanCompleted,
  } = data;

  const {
    isSelected: isShipmentSelected,
    isAllSelected,
    handleSelect,
  } = useSelectionContext();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isDraggedOverContent, setIsDraggedOverContent] = useState(false);

  const { shipments: plannedShipments, isLoading } =
    useInboundPlanningContext();

  const isFiltersLinked = useLinkedFilters();

  const [serviceCenter] = useSelectedServiceCenter();
  const planDate = dateToInt(selectedDate);

  const plans = useMemo(
    () =>
      getFormattedPlannedRoutes({
        sicId: serviceCenter.id,
        date: planDate,
        plannedRoutes: [],
        shipments: plannedShipments,
        timeZone: serviceCenter.timeZone,
      }),
    [serviceCenter.id, planDate, plannedShipments, serviceCenter.timeZone]
  );

  const onSuccessfulAssignment = async (actionType: ShipmentActionTypeId) => {
    const { description } = actionsMap[actionType];

    shipmentActionDialogRef?.current?.close();
    toast(description, {
      variant: "success",
    });

    onClearSidebarSelection?.();
    await onSidebarRefresh?.();
  };

  const { mutate: unassignShipment } = useUnassignShipments({
    onSuccess: async () => {
      await onSuccessfulAssignment("unassign");
    },
    onSettled: () => {
      shipmentActionDialogRef?.current?.close();
      confirmDialogRef?.current?.close();
    },
  });

  const { mutate: assignShipment } = useAssignShipments({
    onSuccess: async () => {
      await onSuccessfulAssignment("assign");
    },
    onSettled: () => {
      shipmentActionDialogRef?.current?.close();
      confirmDialogRef?.current?.close();
    },
  });

  const handleAssignShipments: AssignShipmentFn = useCallback(
    ({ destination, origin, shipmentIds, destinationIndex, location }) => {
      const destinationId = destination.parentId || destination.id || "";
      const originId = origin.parentId || origin.id || "";

      const destinationPlan = plans.find(
        (plan: { id: number }) => Number(destinationId) === plan.id
      );

      const isTableToSidebar = destination.source === "sidebar";
      const idx = destinationIndex;
      const isSidebarHeaderDrop = isTableToSidebar && Number.isNaN(idx);

      const dropMetrics = destinationPlan
        ? getDropMetrics(
            destinationPlan,
            location,
            destinationIndex,
            isSidebarHeaderDrop
          )
        : {
            afterShipmentId: undefined,
            afterRouteStop: null,
            isDroppedOnHeader: true,
          };

      shipmentActionDialogRef?.current?.open("loading", "assign");
      assignShipment({
        origins: [
          {
            id: String(originId),
            type: origin.type,
            shipmentIds,
          },
        ],
        target: {
          date: planDate,
          sicId: serviceCenter.id,
          id: String(destinationId),
          type: destination.type,
          afterShipmentId: dropMetrics.afterShipmentId,
          ...getAfterRouteStopParam(dropMetrics.afterRouteStop),
          isDroppedOnHeader: dropMetrics.isDroppedOnHeader,
        },
      });
    },
    [assignShipment, planDate, serviceCenter.id, shipmentActionDialogRef, plans]
  );

  const handleUnassignShipments: UnassignShipmentFn = useCallback(
    ({ shipmentIds, sourceId = "", sourceType }) => {
      shipmentActionDialogRef?.current?.open("loading", "unassign");
      unassignShipment({
        origins: [
          {
            id: sourceId,
            type: sourceType,
            shipmentIds,
          },
        ],
        target: {
          date: planDate,
          sicId: serviceCenter.id,
          type: "unassign",
        },
      });
    },
    [unassignShipment, planDate, serviceCenter.id, shipmentActionDialogRef]
  );

  const { handleDrop } = useInboundDragAndDrop();

  const shipments = plannedShipments.filter((shipment) =>
    shipmentIds.includes(shipment.id)
  );
  // determine if the current section/route is immutable (DEPT with DLVR shipments)
  const isRouteImmutable = useIsRouteImmutable(status, shipments);

  const handleSelection = () => {
    if (!isEditingAllowed || isTrapCancelled) return;
    handleSelect({
      id,
      type,
      shipmentIds: shipmentIds.map(Number),
    });
  };

  function getType(): SummaryContextType {
    switch (tab) {
      case "appointments":
        return "bucket";
      case "sections":
        return "section";
      case "routes":
        return "route";
      case "traps":
        return "trap";
      default:
        return "bucket";
    }
  }

  const type = getType();

  // determine if the current trap is cancelled (CNCL status)
  const isTrapCancelled = useIsTrapCancelled(
    type === "trap" ? status : undefined
  );

  const handleContext = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    // disable context menu on immutable routes or cancelled traps
    if (!isEditingAllowed || isRouteImmutable || isTrapCancelled) return;
    if (!isAllSelected(shipmentIds, type)) handleSelection();
    contextRef &&
      contextRef.current?.openContextMenu(event, {
        id,
        shipmentIds,
        type,
      });
  };

  const dragData: DraggedItem = useMemo(
    () => ({
      id: String(id),
      source: "sidebar",
      type,
      parentId: String(id),
    }),
    [id, type]
  );

  useEffect(() => {
    const sectionEl = sectionRef.current;
    invariant(sectionEl, "Section component should exist");

    return dropTargetForElements({
      element: sectionEl,
      canDrop: () => isEditingAllowed && !isRouteImmutable,
      onDragStart: () => setIsDraggedOver(true),
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: ({ source, location }) => {
        // Check for cancelled trap
        if (isTrapCancelled) {
          toast(toastMessage.inbound.assign.unableToAssignToCancelledTrap, {
            variant: "warning",
          });
          setIsDraggedOver(false);
          return;
        }

        handleDrop({
          source,
          location,
          handleAssignShipments,
          handleUnassignShipments,
        });
        setIsDraggedOver(false);
      },
      getData: () => dragData,
      getIsSticky: () => false,
    });
  }, [
    id,
    type,
    isEditingAllowed,
    isRouteImmutable,
    isTrapCancelled,
    handleDrop,
    handleAssignShipments,
    handleUnassignShipments,
    dragData,
  ]);

  // Drop target for empty expanded content area
  useEffect(() => {
    // Only proceed if expanded and empty, and element is ready
    if (!isExpanded || shipments.length > 0 || !emptyContentRef.current) {
      return;
    }

    return dropTargetForElements({
      element: emptyContentRef.current,
      canDrop: () => isEditingAllowed && !isRouteImmutable,
      onDragStart: () => setIsDraggedOverContent(true),
      onDragEnter: () => setIsDraggedOverContent(true),
      onDragLeave: () => setIsDraggedOverContent(false),
      onDrop: ({ source, location }) => {
        // Check for cancelled trap
        if (isTrapCancelled) {
          toast(toastMessage.inbound.assign.unableToAssignToCancelledTrap, {
            variant: "warning",
          });
          setIsDraggedOverContent(false);
          return;
        }

        handleDrop({
          source,
          location,
          handleAssignShipments,
          handleUnassignShipments,
        });
        setIsDraggedOverContent(false);
      },
      getData: () => dragData,
      getIsSticky: () => false,
    });
  }, [
    isExpanded,
    shipments.length,
    id,
    type,
    isEditingAllowed,
    isRouteImmutable,
    isTrapCancelled,
    handleDrop,
    handleAssignShipments,
    handleUnassignShipments,
    dragData,
  ]);

  const isSectionPlanned =
    planType || allShipmentsPlanned
      ? "today"
      : allShipmentsPlannedForFuture
        ? "future"
        : undefined;

  const sectionNameMaxWidth = (): number => {
    const widthAdjustments = [isPlanCompleted ? 28 : 0, isCommitted ? 28 : 0];
    return 139 - widthAdjustments.reduce((acc, curr) => acc + curr, 0);
  };

  return (
    <Accordion
      sx={{
        border: 0,
        borderRadius: "0.5rem",
        backgroundColor: isDraggedOver
          ? `${alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)} !important`
          : isSelected
            ? `${theme.palette.action.selected} !important`
            : "unset",
      }}
      expanded={isExpanded}
      slotProps={{ transition: { unmountOnExit: true } }}
      onContextMenu={handleContext}
    >
      <Item ref={sectionRef} onClick={() => handleSelection()}>
        <Summary sx={{ flexWrap: "nowrap" }}>
          <IconButton
            aria-label={
              isExpanded ? `Collapse ${type} ${id}` : `Expand ${type} ${id}`
            }
            sx={{ padding: "4px" }}
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            <ArrowDropDown
              sx={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </IconButton>

          <TableLink
            id={String(id)}
            title={title || ""}
            name={name || ""}
            shipmentIds={shipmentIds}
            inboundPage={planType ? "planning" : inboundPage}
            tab={planType ? "list" : tab || "appointments"}
            iconComponent={
              <InboundTrailerIcon
                isLockedRoute={Boolean(isCommitted)}
                icon={icon}
                size="small"
              />
            }
            planType={planType}
            style={{
              fontWeight: 400,
              width: "100%",
            }}
            paramsToKeep={inboundFilterParamsToKeep(
              isFiltersLinked,
              false,
              true
            )}
          >
            <Typography
              sx={{
                maxWidth: sectionNameMaxWidth(),
                overflow: "hidden",
                position: "relative",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatTrailerTitle(name, title)}
            </Typography>
          </TableLink>
          {isCommitted && <Lock sx={{ color: theme.palette.grey[600] }} />}
          {isPlanCompleted && (
            <CheckCircle style={{ color: theme.palette.primary.main }} />
          )}
          {status && (
            <PlannedStatusTag
              label={status}
              isPlannedFor={isSectionPlanned}
              futurePlanDates={allFuturePlanDates}
              selectedDate={selectedDate}
            />
          )}
        </Summary>
        <Box sx={{ marginLeft: "2rem" }}>
          <ItemSummary sx={{ marginBottom: "4px" }}>
            <TagsGrid tags={tags} maxOverlappedItems={3} />
          </ItemSummary>
          <ItemSummary>
            <Totals>
              <Receipt
                sx={{ fontSize: "1.25rem", color: iconFillColors.darkGray }}
              />
              {formatNumber(bills)}
            </Totals>
            <Totals>
              <PalletsIcon
                sx={{ fontSize: "1.25rem", color: iconFillColors.darkGray }}
              />
              {formatNumber(pallets)}
            </Totals>
            <Totals>
              <PiecesIcon
                sx={{ fontSize: "1.25rem", color: iconFillColors.darkGray }}
              />
              {formatNumber(pieces)}
            </Totals>
            <Totals>
              <MonitorWeight
                sx={{ fontSize: "1.25rem", color: iconFillColors.darkGray }}
              />
              {`${formatNumber(weight)} ${weightUnit}`}
            </Totals>
          </ItemSummary>
        </Box>
      </Item>
      <AccordionDetails sx={{ padding: 0, border: 0 }}>
        {isLoading && (
          <Box sx={{ padding: "1rem" }}>
            <Loading size={32} />
          </Box>
        )}
        {shipments.sort(sortShipmentsByStopSequence).map((shipment, index) => (
          <Fragment key={shipment.id}>
            <ShipmentItem
              index={index}
              canReorder={canReorder}
              isDraggable={
                isEditingAllowed && !isRouteImmutable && !isTrapCancelled
              }
              isEditingAllowed={isEditingAllowed}
              isRouteImmutable={isRouteImmutable}
              isTrapCancelled={isTrapCancelled}
              parentId={id}
              shipment={shipment}
              type={type}
              contextRef={contextRef}
              isSelected={isShipmentSelected(shipment.id, type)}
              routeStatus={status}
            />
          </Fragment>
        ))}
        {shipments.length === 0 && !isLoading && (
          <Box
            ref={emptyContentRef}
            sx={{
              backgroundColor: isDraggedOverContent
                ? `${alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)}`
                : "transparent",
              transition: "background-color 0.2s ease",
              minHeight: "60px",
            }}
          >
            <NoResultFound>
              <ContentPaste sx={{ fontSize: "2rem" }} />
              <Typography>No shipments found.</Typography>
            </NoResultFound>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default memo(PlanningDroppableSectionItem);
